import { createTheme } from '@mui/material/styles';

const baseThemeOptions = (darkMode) => ({
    palette: {
        mode: darkMode ? 'dark' : 'light',
    },
});

export function createProjectManagementTheme(darkMode) {
    return createTheme({
        ...baseThemeOptions(darkMode),
        palette: {
            mode: darkMode ? 'dark' : 'light',
            primary: { main: '#1976d2' },
            secondary: { main: '#dc004e' },
        },
    });
}

export function createVendorTheme(darkMode) {
    return createTheme({
        ...baseThemeOptions(darkMode),
        palette: {
            mode: darkMode ? 'dark' : 'light',
            primary: { main: '#2e7d32' },
            secondary: { main: '#66bb6a' },
        },
    });
}

export function createCustomerTheme() {
    return createTheme({
        palette: {
            mode: 'dark',
            primary: { main: '#d4af37', contrastText: '#121212' },
            secondary: { main: '#c62828' },
            text: {
                primary: '#f5f5f5',
                secondary: 'rgba(255, 255, 255, 0.7)',
            },
            background: {
                default: '#121212',
                paper: '#1e1e1e',
            },
            divider: 'rgba(255, 255, 255, 0.12)',
        },
    });
}

export function createHubSectionTheme(organizationType, darkMode) {
    switch (organizationType) {
        case 'Vendor':
            return createVendorTheme(darkMode);
        case 'Customer':
            return createCustomerTheme();
        default:
            return createProjectManagementTheme(darkMode);
    }
}

/** Landing-page card colors per hub (explicit contrast, independent of app theme). */
export function getHubSectionStyles(hubType, darkMode = false) {
    switch (hubType) {
        case 'Vendor':
            return {
                headerBg: '#1b5e20',
                headerText: '#ffffff',
                headerMuted: 'rgba(255, 255, 255, 0.88)',
                bodyBg: darkMode ? '#1a2e1b' : '#eef6ee',
                bodyText: darkMode ? '#e8f5e9' : '#1b3d1f',
                bodyMuted: darkMode ? 'rgba(232, 245, 233, 0.72)' : 'rgba(27, 61, 31, 0.65)',
                assistantBubbleBg: darkMode ? 'rgba(255, 255, 255, 0.07)' : '#ffffff',
                divider: darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(27, 94, 32, 0.15)',
                border: '#2e7d32',
                buttonBg: '#2e7d32',
                buttonHover: '#1b5e20',
                buttonText: '#ffffff',
                rowButtonColor: darkMode ? '#a5d6a7' : '#2e7d32',
            };
        case 'Customer':
            return {
                headerBg: '#1a0a0a',
                headerText: '#f5f0e1',
                headerMuted: 'rgba(245, 240, 225, 0.82)',
                bodyBg: '#242424',
                bodyText: '#f5f5f5',
                bodyMuted: 'rgba(255, 255, 255, 0.65)',
                assistantBubbleBg: 'rgba(255, 255, 255, 0.06)',
                divider: 'rgba(255, 255, 255, 0.1)',
                border: '#c62828',
                buttonBg: '#d4af37',
                buttonHover: '#b8962e',
                buttonText: '#1a0a0a',
                rowButtonColor: '#d4af37',
            };
        default:
            return {
                headerBg: '#1565c0',
                headerText: '#ffffff',
                headerMuted: 'rgba(255, 255, 255, 0.9)',
                bodyBg: darkMode ? '#1a2332' : '#f0f6fc',
                bodyText: darkMode ? '#e3f2fd' : '#0d3c61',
                bodyMuted: darkMode ? 'rgba(227, 242, 253, 0.72)' : 'rgba(13, 60, 97, 0.65)',
                assistantBubbleBg: darkMode ? 'rgba(255, 255, 255, 0.07)' : '#ffffff',
                divider: darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(21, 101, 192, 0.15)',
                border: '#1976d2',
                buttonBg: '#1976d2',
                buttonHover: '#1565c0',
                buttonText: '#ffffff',
                rowButtonColor: darkMode ? '#90caf9' : '#1976d2',
            };
    }
}

export function getHubDialogPaperSx(styles, extraSx = {}) {
    return {
        borderRadius: 3,
        overflow: 'hidden',
        border: 2,
        borderColor: styles.border,
        ...extraSx,
    };
}

export function getHubFieldSx(styles) {
    return {
        '& .MuiInputLabel-root': { color: styles.bodyMuted },
        '& .MuiOutlinedInput-root': {
            color: styles.bodyText,
            '& fieldset': { borderColor: styles.divider },
            '&:hover fieldset': { borderColor: styles.border },
            '&.Mui-focused fieldset': { borderColor: styles.border },
        },
        '& .MuiSelect-icon': { color: styles.bodyMuted },
        '& .MuiFormHelperText-root': { color: styles.bodyMuted },
    };
}

export function getHubDialogCancelButtonSx(styles) {
    return { color: styles.bodyMuted };
}

export function getHubDialogSubmitButtonSx(styles) {
    return {
        bgcolor: styles.buttonBg,
        color: styles.buttonText,
        fontWeight: 600,
        '&:hover': { bgcolor: styles.buttonHover },
    };
}

export function getHubDialogActionsSx(styles) {
    return {
        bgcolor: styles.bodyBg,
        px: 2.5,
        pb: 2.5,
        pt: 0,
    };
}

export function getHubDialogContentSx(styles) {
    return {
        bgcolor: styles.bodyBg,
        pt: 2.5,
        pb: 1,
        color: styles.bodyText,
    };
}

/** Right-anchored drawer shell — mirrors HubDialog paper, header, body, and footer. */
export function getHubDrawerPaperSx(styles, extraSx = {}) {
    return {
        borderRadius: 0,
        borderTopLeftRadius: 24,
        borderBottomLeftRadius: 24,
        overflow: 'hidden',
        border: 2,
        borderRight: 0,
        borderColor: styles.border,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: (theme) => theme.shadows[16],
        ...extraSx,
    };
}

export function getHubDrawerHeaderSx(styles) {
    return {
        bgcolor: styles.headerBg,
        color: styles.headerText,
        px: 2.5,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexShrink: 0,
    };
}

export function getHubDrawerHeaderIconButtonSx(styles) {
    return {
        color: styles.headerText,
        '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.12)',
        },
    };
}

export function getHubDrawerBodySx(styles, extraSx = {}) {
    return {
        flex: 1,
        overflow: 'auto',
        px: 2.5,
        py: 2,
        bgcolor: styles.bodyBg,
        color: styles.bodyText,
        ...extraSx,
    };
}

export function getHubDrawerFooterSx(styles) {
    return {
        p: 2.5,
        pt: 2,
        bgcolor: styles.bodyBg,
        borderTop: 1,
        borderColor: styles.divider,
        flexShrink: 0,
    };
}

export function getHubDrawerContextBarSx(styles) {
    return {
        px: 2.5,
        py: 1.25,
        bgcolor: styles.bodyBg,
        borderBottom: 1,
        borderColor: styles.divider,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexShrink: 0,
    };
}

export function getHubPromptCardSx(styles) {
    return {
        p: 1.5,
        cursor: 'pointer',
        bgcolor: styles.bodyBg,
        color: styles.bodyText,
        border: 1,
        borderColor: styles.divider,
        borderRadius: 2,
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
            borderColor: styles.border,
            boxShadow: 1,
        },
    };
}
